import * as Sentry from '@sentry/vue'
import { getClient, getFeedback } from '@sentry/vue'
import { useI18n } from 'vue-i18n'
import { onScopeDispose, ref, watch, type Ref } from 'vue'
import { colors, borderRadius } from '@/design-tokens'
// Import from separate file to avoid vue-i18n pre-compilation
import feedbackLabels from '@/sentry-feedback-labels.json'
import { SENTRY_FEEDBACK_HOST_ID } from '@/constants/sentryFeedback'

type FeedbackMessages = (typeof feedbackLabels)['nl']

/**
 * Get the feedback labels from a messages object.
 */
function getFeedbackLabelsFromMessages(messages: FeedbackMessages) {
  return {
    triggerLabel: messages.triggerLabel,
    submitButtonLabel: messages.submitButtonLabel,
    cancelButtonLabel: messages.cancelButtonLabel,
    formTitle: messages.formTitle,
    nameLabel: messages.nameLabel,
    namePlaceholder: messages.namePlaceholder,
    emailLabel: messages.emailLabel,
    emailPlaceholder: messages.emailPlaceholder,
    messageLabel: messages.messageLabel,
    messagePlaceholder: messages.messagePlaceholder,
    successMessageText: messages.successMessageText,
  }
}

/**
 * Shared theme configuration for feedback widget.
 */
const feedbackTheme = {
  themeLight: {
    background: colors.background.content,
    foreground: colors.text.primary,
    accentBackground: colors.brand.primary,
    accentForeground: colors.common.white,
    submitBackground: colors.button.primary,
    submitBackgroundHover: colors.button.primaryHover,
    submitForeground: colors.button.primaryText,
    cancelBackground: 'transparent',
    cancelBackgroundHover: colors.background.secondary,
    cancelForeground: colors.text.primary,
    inputBackground: colors.background.content,
    inputForeground: colors.text.primary,
    inputBorder: colors.border.input,
    inputOutlineFocus: colors.border.inputFocus,
    formBorderRadius: borderRadius.lg,
    formContentBorderRadius: borderRadius.md,
  },
  themeDark: {
    background: '#1a2742',
    foreground: colors.common.white,
    accentBackground: colors.brand.primary,
    accentForeground: colors.common.white,
    submitBackground: colors.button.primary,
    submitBackgroundHover: colors.button.primaryHover,
    submitForeground: colors.button.primaryText,
    cancelBackground: 'transparent',
    cancelBackgroundHover: 'rgba(255, 255, 255, 0.1)',
    cancelForeground: colors.common.white,
    inputBackground: 'rgba(255, 255, 255, 0.1)',
    inputForeground: colors.common.white,
    inputBorder: 'rgba(255, 255, 255, 0.2)',
    inputOutlineFocus: colors.brand.primary,
    formBorderRadius: borderRadius.lg,
    formContentBorderRadius: borderRadius.md,
  },
}

/**
 * Z-index for the Sentry feedback dialog.
 *
 * Above modal overlays (z-index: 1000). Until #4694 it sat below them, on the
 * reasoning that the dialog could only be opened from the side menu, which a
 * modal covers — so a feedback dialog and a modal were never both on screen. That
 * was the defect: the screen a behandelaar wants to report on is often the open
 * Modal, SlideOutPanel or BottomSheet (SCRIVIO-FE-HK), so each now carries a
 * trigger in its header and the dialog has to open over it. Tooltips (9999) still
 * go above it.
 */
const SENTRY_FEEDBACK_Z_INDEX = 1100

/**
 * Get the full feedback integration options including styling and labels.
 */
function getFeedbackOptions(labels: ReturnType<typeof getFeedbackLabelsFromMessages>) {
  return {
    colorScheme: 'system' as const,
    // OFF, and it may not be turned back on without solving the masking problem
    // first. This is a raw screen capture, not an rrweb DOM recording, so none of
    // the replay masking `main.ts` pins can reach it: `maskAllText`, `maskAllInputs`
    // and `blockAllMedia` all act on the replay's serialised DOM. A behandelaar
    // reporting a problem from a dossier photographs whatever is on screen — a
    // cliënt's name, BSN, diagnosis — and it is stored by a third-party processor
    // that has no basis recorded for holding it (#4302, #4202).
    //
    // 19 of the 25 submissions in the 14 days before this landed carried one.
    enableScreenshot: false,
    // The SDK's own floating actor button is not injected: it is pinned to the
    // bottom-right corner, which is exactly where a behandelaar grabs the agenda
    // to drag an afspraak (SCRIVIO-FE-CM, #4061). The trigger is a side-menu item
    // instead — see `useSentryFeedbackTrigger`. Everything below still applies:
    // the dialog, its labels, its theme and the submission path stay Sentry's.
    autoInject: false,
    zIndex: SENTRY_FEEDBACK_Z_INDEX,
    id: SENTRY_FEEDBACK_HOST_ID,
    // Open the form on the account the reporter is signed in as, taken from the Sentry
    // user scope that `setSentryUserContext` writes at login. A typed address is
    // self-reported and was wrong on both of the reports that prompted this — one a
    // personal Gmail address, one a colleague's (#3598).
    //
    // This mapping is also the SDK's default; it is stated because a change to that
    // default would silently put us back on typed addresses, which is exactly the
    // failure that is hard to notice.
    useSentryUser: { email: 'email', name: 'username' },
    ...labels,
    ...feedbackTheme,
  }
}

/**
 * Bumped every time a feedback integration is added to the client.
 *
 * `attachTo` closes over the integration it was called on, so a trigger bound
 * once at mount keeps opening the dialog of whichever integration happened to be
 * installed then — with the old locale's labels — while looking exactly like one
 * that works. {@link useSentryFeedbackTrigger} re-attaches to whatever
 * `getFeedback()` answers with on each bump, so the trigger follows the client
 * rather than an instance.
 */
const integrationRevision = ref(0)

/**
 * Update the Sentry feedback integration with new locale labels.
 */
function updateFeedbackIntegration(t: (key: string) => string): void {
  const client = getClient()
  if (!client) return

  const labels = {
    triggerLabel: t('sentry.feedback.triggerLabel'),
    submitButtonLabel: t('sentry.feedback.submitButtonLabel'),
    cancelButtonLabel: t('sentry.feedback.cancelButtonLabel'),
    formTitle: t('sentry.feedback.formTitle'),
    nameLabel: t('sentry.feedback.nameLabel'),
    namePlaceholder: t('sentry.feedback.namePlaceholder'),
    emailLabel: t('sentry.feedback.emailLabel'),
    emailPlaceholder: t('sentry.feedback.emailPlaceholder'),
    messageLabel: t('sentry.feedback.messageLabel'),
    messagePlaceholder: t('sentry.feedback.messagePlaceholder'),
    successMessageText: t('sentry.feedback.successMessageText'),
  }

  // Guard: only update if all labels are valid strings
  // This prevents issues when i18n returns objects during initialization
  const allStrings = Object.values(labels).every((v) => typeof v === 'string')
  if (!allStrings) {
    console.warn('Sentry feedback labels contain non-string values, skipping update')
    return
  }

  // Create and add a new feedback integration with the updated labels.
  //
  // ⚠️ This does not currently take effect: `Client.addIntegration` delegates to
  // `setupIntegration`, which returns early when an integration of that name is
  // already installed (@sentry/vue 10.40), so `getFeedback()` keeps answering
  // with the integration `Sentry.init` installed and the dialog keeps its
  // initial labels. That is #4069, not this composable's caller's problem — the
  // trigger below is written to follow the client either way, so it is correct
  // both now and once the replacement really happens.
  const feedbackIntegration = Sentry.feedbackIntegration(getFeedbackOptions(labels))
  client.addIntegration(feedbackIntegration)
  integrationRevision.value++
}

/**
 * Wire an element of ours up as the feedback trigger.
 *
 * The dialog and the submission path stay Sentry's — `attachTo` is the SDK's own
 * hook for a caller-owned trigger — so only *where the button sits* changed. The
 * options the dialog opens with, including the `useSentryUser` prefill, are the
 * integration's; a trigger cannot and does not restate them.
 *
 * @param triggerEl Template ref of the element that should open the dialog.
 * @returns `isAvailable` — false when Sentry is not initialised (no DSN: local
 *   dev, e2e), so the caller can leave the control out rather than render a
 *   button that does nothing.
 */
export function useSentryFeedbackTrigger(triggerEl: Ref<HTMLElement | null>): {
  isAvailable: Ref<boolean>
} {
  // Answered by the immediate watch below, which runs before the first render —
  // so a caller's `v-if` never flickers a button that is about to disappear.
  const isAvailable = ref(false)
  let detach: (() => void) | undefined

  function attach(): void {
    detach?.()
    detach = undefined

    const feedback = getFeedback()
    isAvailable.value = feedback !== undefined
    if (!feedback || !triggerEl.value) return

    const el = triggerEl.value
    // The SDK's dialog hands focus back to nothing when it closes. A trigger inside
    // a Modal must get it back, or the next Tab starts from `<body>` behind the
    // overlay's focus trap (#4694).
    const returnFocus = () => {
      if (el.isConnected) el.focus()
    }
    detach = feedback.attachTo(el, { onFormClose: returnFocus, onFormSubmitted: returnFocus })
  }

  // The element arrives after the first render, and a locale change re-adds the
  // integration — both have to (re-)attach.
  watch([triggerEl, integrationRevision], attach, { immediate: true })
  onScopeDispose(() => detach?.())

  return { isAvailable }
}

/**
 * Composable to manage Sentry feedback widget translations.
 * Watches for locale changes and updates the feedback widget labels accordingly.
 * Must be called within a Vue component setup context.
 */
export function useSentryFeedback(): void {
  const { t, locale } = useI18n()

  // Watch for locale changes and update feedback widget
  // Only updates when locale actually changes (not on initial mount)
  watch(
    locale,
    (newLocale, oldLocale) => {
      // Skip if this is the initial mount (oldLocale undefined) or no real change
      if (oldLocale === undefined || newLocale === oldLocale) {
        return
      }
      updateFeedbackIntegration(t)
    },
    { immediate: false }
  )
}

/**
 * Get feedback integration options for initial Sentry setup.
 * Uses Dutch messages directly since i18n is not yet available at init time.
 */
export function getInitialFeedbackOptions() {
  const labels = getFeedbackLabelsFromMessages(feedbackLabels.nl)
  return getFeedbackOptions(labels)
}
